import { API_BASE_URL } from "../config/config";

type EventData = Record<string, any> | undefined;

interface DriverEvent {
  ts: string;
  type: string;
  sales_order_id: string | null;
  delivery_id: string | null;
  device_id: string | null;
  vendor_id: string | null;
  route: string;
  data?: EventData;
}

const ENDPOINT = API_BASE_URL + "/orders/driver-log";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 100;

let buffer: DriverEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

function readCtx() {
  return {
    sales_order_id: localStorage.getItem("sales_order_id"),
    delivery_id: localStorage.getItem("delivery_id"),
    device_id: localStorage.getItem("device_id"),
    vendor_id: localStorage.getItem("vendor_id"),
    route: window.location.pathname + window.location.search,
  };
}

export function logEvent(type: string, data?: EventData) {
  try {
    const ctx = readCtx();
    buffer.push({
      ts: new Date().toISOString(),
      type,
      ...ctx,
      data,
    });
    if (buffer.length >= MAX_BUFFER) {
      flush();
    }
  } catch {
    // swallow — logger must never crash the app
  }
}

export function flush(useBeacon = false): void {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];
  const payload = JSON.stringify({ events });

  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // network blip — drop silently
    });
  } catch {
    // never throw
  }
}

export function startDriverLogger() {
  if (started) return;
  started = true;

  // First breadcrumb on every boot — also tells us if a refresh happened
  logEvent("app_boot", {
    href: window.location.href,
    referrer: document.referrer || null,
    ua: navigator.userAgent,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio,
  });

  // Periodic flush
  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);

  // Flush on hide / unload — uses sendBeacon so the request survives page death
  window.addEventListener("pagehide", () => flush(true));
  window.addEventListener("beforeunload", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) flush(true);
  });

  // Catch uncaught errors
  window.addEventListener("error", (e) => {
    logEvent("window_error", {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    logEvent("unhandled_rejection", {
      reason: String((e as any).reason?.message ?? (e as any).reason ?? ""),
    });
  });
}

export function stopDriverLogger() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush(true);
  started = false;
}
